import { createTheme } from '@mui/material/styles'
import type {} from '@mui/x-data-grid/themeAugmentation'
import { DATA_VIZ, BRAND, SURFACE, TEXT } from './brand-tokens'

/**
 * Theme Configuration for MUI Dashboard
 * 
 * This file contains all the brand styling, colors, spacing, and component overrides
 * for the Material-UI theme system. All visual customizations should be made here
 * to ensure consistency across the application.
 * 
 * Key Areas:
 * - Brand Colors: Core color palette for the application
 * - Layout Spacing: Consistent spacing and dimensions 
 * - Component Overrides: Custom styling for MUI components
 * - Theme Extensions: Custom theme properties for specialized components
 */

export interface CustomShadows {
  z1: string;
  z4: string;
  z8: string;
  z12: string;
  z16: string;
  z20: string;
  z24: string;
  primary: string;
  secondary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  card: string;
  dialog: string;
  dropdown: string;
}

// Extend the Theme interface
declare module '@mui/material/styles' {
  interface Theme {
    customShadows: CustomShadows
    sidebar: {
      width: number
      collapsedWidth: number
      iconSize: number
      itemHeight: number
      logoHeight: number
      transition: string
      iconBorderRadius: number
      activeIconBorderRadius: string
      iconPosition: {
        expanded: number
        collapsed: string
      }
      textPadding: string
    }
    design: {
      canvas: {
        backgroundColor: string
        borderColor: string
        gridColor: string
        height: number
        gridSize: number
        mailPiece: {
          aspectRatio: string
          border: string
          borderDark: string
          borderRadius: number
        }
      }
      toolbar: {
        backgroundColor: string
        iconSize: number
        spacing: number
        width: number
      }
      panel: {
        width: number
        backgroundColor: string
        borderColor: string
      }
    }
    journeyBuilder: {
      node: {
        minWidth: number
        maxWidth: number
        padding: number
        iconSize: number
        handleSize: number
      }
      sidebar: {
        width: number
        itemPadding: number
      }
      detailsPanel: {
        width: number
      }
      edge: {
        strokeWidth: number
        strokeColor: string
        dashArray: string
      }
      toolbox: {
        flatItem: {
          borderRadius: number
          size: number
        }
        colors: {
          audience: { background: string; text: string; border: string }
          campaign: { background: string; text: string; border: string }
          directMail: { background: string; text: string; border: string }
          digitalAd: { background: string; text: string; border: string }
          condition: { background: string; text: string; border: string }
          action: { background: string; text: string; border: string }
          tool: { background: string; text: string; border: string }
        }
      }
      sections: {
        startIcon: string
        channelsIcon: string
        conditionsIcon: string
        actionsIcon: string
        toolsIcon: string
      }
      quickStart: {
        background: string
        border: string
        icon: string
        title: string
        text: string
      }
    }
    charts: {
      legend: {
        markerSize: number
        markerRadius: number
        gap: number
        itemGap: number
        fontSize: number
        fontWeight: number
        marginTop: number
      }
      background: {
        areaOpacity: number
        lineOpacity: number
        lineWidth: number
      }
      bars: {
        categoryGapRatio: number
        barGapRatio: number
      }
    }
  }
  interface ThemeOptions {
    customShadows?: CustomShadows
    sidebar?: {
      width?: number
      collapsedWidth?: number
      iconSize?: number
      itemHeight?: number
      logoHeight?: number
      transition?: string
      iconBorderRadius?: number
      activeIconBorderRadius?: string
      iconPosition?: {
        expanded?: number
        collapsed?: string
      }
      textPadding?: string
    }
    design?: {
      canvas?: {
        backgroundColor?: string
        borderColor?: string
        gridColor?: string
        height?: number
        gridSize?: number
        mailPiece?: {
          aspectRatio?: string
          border?: string
          borderDark?: string
          borderRadius?: number
        }
      }
      toolbar?: {
        backgroundColor?: string
        iconSize?: number
        spacing?: number
        width?: number
      }
      panel?: {
        width?: number
        backgroundColor?: string
        borderColor?: string
      }
    }
    journeyBuilder?: {
      node?: {
        minWidth?: number
        maxWidth?: number
        padding?: number
        iconSize?: number
        handleSize?: number
      }
      sidebar?: {
        width?: number
        itemPadding?: number
      }
      detailsPanel?: {
        width?: number
      }
      edge?: {
        strokeWidth?: number
        strokeColor?: string
        dashArray?: string
      }
      toolbox?: {
        flatItem?: {
          borderRadius?: number
          size?: number
        }
        colors?: Partial<{
          audience: { background: string; text: string; border: string }
          campaign: { background: string; text: string; border: string }
          directMail: { background: string; text: string; border: string }
          digitalAd: { background: string; text: string; border: string }
          condition: { background: string; text: string; border: string }
          action: { background: string; text: string; border: string }
          tool: { background: string; text: string; border: string }
        }>
      }
      sections?: {
        startIcon?: string
        channelsIcon?: string
        conditionsIcon?: string
        actionsIcon?: string
        toolsIcon?: string
      }
      quickStart?: {
        background?: string
        border?: string
        icon?: string
        title?: string
        text?: string
      }
    }
    charts?: {
      legend?: {
        markerSize?: number
        markerRadius?: number
        gap?: number
        itemGap?: number
        fontSize?: number
        fontWeight?: number
        marginTop?: number
      }
      background?: {
        areaOpacity?: number
        lineOpacity?: number
        lineWidth?: number
      }
      bars?: {
        categoryGapRatio?: number
        barGapRatio?: number
      }
    }
  }

  // Extend palette to include brand colors
  interface Palette {
    brand: {
      darkTeal: string
      deepTeal?: string
      teal: string
      lightTeal: string
      yellow: string
      lightYellow: string
      tealHover: string
      deepRed?: string
    }
    nav: {
      activeIconBg: string
      activeIconColor: string
      inactiveIconColor: string
      activeText: string
      inactiveText: string
    }
    surface?: {
      sidebar: string
      card: string
    }
    status: {
      success: string
      warning: string
    }
    hover: {
      lightTeal: string
    }
  }

  interface Color {
    '500Channel'?: string;
  }

  interface PaletteOptions {
    brand?: {
      // Teal family (dark to light)
      darkTeal?: string
      deepTeal?: string
      teal?: string
      mediumTeal?: string
      lightTeal?: string
      // Yellow family (dark to light)
      yellow?: string
      softYellow?: string
      lightYellow?: string
      // Accent reds
      deepRed?: string
      // Neutrals
      white?: string
      lightGray?: string
      darkGray?: string
      // Alerts & Status (exceptions to main palette)
      error?: string
      warning?: string
      success?: string
      info?: string
      // Legacy
      tealHover?: string
    }
    nav?: {
      activeIconBg?: string
      activeIconColor?: string
      inactiveIconColor?: string
      activeText?: string
      inactiveText?: string
    }
    surface?: {
      sidebar: string
      card?: string
    }
    status?: {
      success?: string
      warning?: string
    }
    hover?: {
      lightTeal?: string
    }
  }

  interface PaletteColor {
    lighter?: string
    darker?: string
    mainChannel?: string
    lightChannel?: string
    darkChannel?: string
    lighterChannel?: string
    darkerChannel?: string
  }

  interface SimplePaletteColorOptions {
    lighter?: string
    darker?: string
    mainChannel?: string
    lightChannel?: string
    darkChannel?: string
    lighterChannel?: string
    darkerChannel?: string
  }

  interface TypeBackground {
    neutral?: string;
  }
}

/**
 * Brand Color Palette
 * 
 * Define your core brand colors here. These colors are used throughout
 * the application and should match your brand guidelines.
 * 
 * IMPORTANT: The design system is restricted to ONLY use these colors,
 * with the exception of error/alert states which have their own colors.
 */
const BRAND_COLORS = {
  // Teal family (ordered dark → light by gamma/brightness)
  darkTeal: BRAND.navy,     // Darkest - Headers & Emphasis (brand-navy)
  deepTeal: '#0D5C61',      // Dark Mode Accents
  teal: BRAND.teal,         // Primary Brand Color (brand-teal v3.0)
  mediumTeal: '#3AAFA9',    // Interactive Elements
  lightTeal: '#58C3BE',     // Accents & Hover States — lighter brand teal

  // Yellow family (ordered dark → light by gamma/brightness)
  yellow: BRAND.yellow,       // Warnings & Highlights
  softYellow: '#f4db7d',      // Background Accents
  lightYellow: BRAND.yellowLight, // Subtle Highlights

  // Neutrals (grayscale)
  white: '#FFFFFF',         // Pure white
  lightGray: '#E0E0E0',     // Light gray for borders, backgrounds
  darkGray: '#4A4A4A',      // Dark gray for secondary text

  // Alert & Status Colors (EXCEPTIONS - only for error/alert UI)
  error: '#D32F2F',         // Error red
  warning: '#F57C00',       // Warning orange
  success: '#3AAFA9',       // Success - medium teal (using primary color shade)
  info: '#009688',          // Info teal

  // Deep red accent requested for heatmap
  deepRed: '#A71B1C',

  // Text colors optimized for readability
  lightModeText: '#1F2937',      // Primary text color for light theme - darker for better contrast
  darkModeText: '#ffffff',       // Primary text color for dark theme - pure white for better contrast
  darkModeSecondary: '#b0bec5',  // Secondary text color for dark theme - light gray

  // Legacy colors (maintained for backward compatibility)
  primary: '#58C3BE',
  primaryDark: '#58C3BE',
  secondary: '#00B894',
  secondaryDark: '#55EFC4',
}

const GREY = {
  '50': '#F9FAFB',
  '100': '#F3F4F6',
  '200': '#E5E7EB',
  '300': '#D1D5DB',
  '400': '#9CA3AF',
  '500': '#919EAB',
  '600': '#6B7280',
  '700': '#4B5563',
  '800': '#1F2937',
  '900': '#111827',
  '500Channel': '145 158 171',
}

const DARK_GREY = {
  '50': BRAND_COLORS.darkTeal,
  '100': BRAND_COLORS.teal,
  '200': BRAND_COLORS.teal,
  '300': TEXT.dark.primary,
  '400': TEXT.dark.secondary,
  '500': TEXT.dark.tertiary,
  '600': TEXT.dark.quaternary,
  '700': SURFACE.dark.active,
  '800': SURFACE.dark.hover,
  '900': SURFACE.dark.app,
  '500Channel': '122 139 146',
}

/**
 * Layout and Spacing Constants
 * 
 * Consistent spacing values based on 8px Material-UI grid system.
 * All dimensions should use these values for visual consistency.
 */
const BRAND_SPACING = {
  // Sidebar and navigation dimensions
  drawerWidth: 300,           // Main sidebar width (increased to prevent text cropping)
  collapsedDrawerWidth: 72,   // Collapsed sidebar width
  iconSize: 40,               // Standard icon container size
  navigationHeight: 48,       // Navigation item height
  logoHeight: 80,             // Logo container height
}

/**
 * Layout Breakpoints
 * 
 * Custom breakpoint values for responsive design
 */
const BRAND_BREAKPOINTS = {
  tablet: 900,    // Tablet breakpoint
  mobile: 600,    // Mobile breakpoint
}

/**
 * Animation and Transition Settings
 * 
 * Consistent timing and easing for smooth user interactions.
 */
const BRAND_TRANSITIONS = {
  smooth: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',  // Standard smooth transition
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',           // Material Design easing
  duration: '0.4s',                                   // Standard duration
}

/** Align all DataGrids with side panels (e.g. DetailsSidePanel subtitle2 labels). */
const DATA_GRID_TYPOGRAPHY = {
  fontSize: '0.875rem',
  '@media (max-width:599.95px)': {
    fontSize: '0.8rem',
  },
} as const

/** DESIGN_SYSTEM.md's DataGrid (Tables) spec: same size as cells (0.875rem/mobile 0.8rem), weight 700, 0.5px tracking. */
const TABLE_HEAD_TOKENS = {
  fontSize: '0.875rem',
  '@media (max-width:599.95px)': {
    fontSize: '0.8rem',
  },
  fontWeight: 700,
  letterSpacing: '0.5px',
  color: 'var(--text-tertiary)',
} as const

/** Vertically center cell and header content (MUI grid cells are flex containers). */
const DATA_GRID_VERTICAL_ALIGN = {
  display: 'flex',
  alignItems: 'center',
} as const

/** Default horizontal alignment; center/right columns keep MUI modifier classes (see root overrides). */
const DATA_GRID_JUSTIFY_START = {
  justifyContent: 'flex-start',
} as const

const DATA_GRID_ROOT_ALIGNMENT_FIXES = {
  '& .MuiDataGrid-cell--textCenter': {
    justifyContent: 'center',
  },
  '& .MuiDataGrid-cell--textRight': {
    justifyContent: 'flex-end',
  },
  '& .MuiDataGrid-columnHeader--alignCenter': {
    justifyContent: 'center',
  },
  '& .MuiDataGrid-columnHeader--alignRight': {
    justifyContent: 'flex-end',
  },
  // Headers default to left when headerAlign is unset (no --alignLeft class in MUI).
  // Exclude checkbox columns, which should be centered.
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--alignCenter):not(.MuiDataGrid-columnHeader--alignRight):not(.MuiDataGrid-columnHeaderCheckbox)':
    {
      justifyContent: 'flex-start',
    },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--alignCenter):not(.MuiDataGrid-columnHeader--alignRight):not(.MuiDataGrid-columnHeaderCheckbox) .MuiDataGrid-columnHeaderDraggableContainer':
    {
      justifyContent: 'flex-start',
    },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--alignCenter):not(.MuiDataGrid-columnHeader--alignRight):not(.MuiDataGrid-columnHeaderCheckbox) .MuiDataGrid-columnHeaderTitleContainer':
    {
      justifyContent: 'flex-start',
    },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--alignCenter):not(.MuiDataGrid-columnHeader--alignRight):not(.MuiDataGrid-columnHeaderCheckbox) .MuiDataGrid-columnHeaderTitleContainerContent':
    {
      justifyContent: 'flex-start',
    },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--alignCenter):not(.MuiDataGrid-columnHeader--alignRight):not(.MuiDataGrid-columnHeaderCheckbox) .MuiDataGrid-columnHeaderTitle':
    {
      textAlign: 'left',
    },
  '& .MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitle': {
    textAlign: 'center',
  },
  '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitle': {
    textAlign: 'right',
  },
} as const

/**
 * Journey Builder Component Configuration
 * 
 * Specific styling values for the Journey Builder flowchart component.
 * Modify these values to adjust the appearance of nodes, edges, and panels.
 */
const JOURNEY_BUILDER = {
  node: {
    minWidth: 240,    // Minimum node card width
    maxWidth: 280,    // Maximum node card width  
    padding: 24,      // Internal padding (3 * 8px spacing)
    iconSize: 36,     // Icon container size within nodes
    handleSize: 12,   // Connection handle size
  },
  sidebar: {
    width: 280,       // Left sidebar width
    itemPadding: 16,  // Padding for sidebar items (2 * 8px spacing)
  },
  detailsPanel: {
    width: 320,       // Right details panel width
  },
  edge: {
    strokeWidth: 2,                    // Connection line thickness
    strokeColor: BRAND_COLORS.lightModeText,  // Connection line color (updated per theme)
    dashArray: '5,5',                  // Dashed line pattern
  }
}

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1B8187',         // --accent-text light: --brand-teal
      lighter: '#D1ECED',
      darker: '#0D5C61',
      mainChannel: '27 129 135',
      lighterChannel: '88 195 190',
      darkerChannel: '13 92 97'
    },
    secondary: {
      main: BRAND_COLORS.yellow,
      lighter: BRAND_COLORS.lightYellow,
      darker: BRAND_COLORS.yellow,
      mainChannel: '236 191 45',
      lighterChannel: '252 242 141',
      darkerChannel: '236 191 45'
    },
    info: {
      lighter: '#B2DFDB',
      light: '#4DB6AC',
      main: '#009688',
      dark: '#00796B',
      darker: '#004D40',
      contrastText: '#FFFFFF',
      mainChannel: '0 150 136',
      lightChannel: '77 182 172',
      darkChannel: '0 121 107',
    },
    background: { default: '#F7FAFB', paper: '#FFFFFF' },
    divider: 'rgba(13, 36, 48, 0.10)',
    text: {
      primary: '#0D2430',
      secondary: '#455A64', // Darker gray for better visibility
      disabled: '#78909C', // More visible disabled state
    },
    // Custom brand colors accessible via theme.palette
    brand: {
      // Teal family
      darkTeal: BRAND_COLORS.darkTeal,
      deepTeal: BRAND_COLORS.deepTeal,
      teal: BRAND_COLORS.teal,
      mediumTeal: BRAND_COLORS.mediumTeal,
      lightTeal: BRAND_COLORS.lightTeal,
      deepRed: BRAND_COLORS.deepRed,
      // Yellow family
      yellow: BRAND_COLORS.yellow,
      softYellow: BRAND_COLORS.softYellow,
      lightYellow: BRAND_COLORS.lightYellow,
      // Neutrals
      white: BRAND_COLORS.white,
      lightGray: BRAND_COLORS.lightGray,
      darkGray: BRAND_COLORS.darkGray,
      // Alert colors (exceptions)
      error: BRAND_COLORS.error,
      warning: BRAND_COLORS.warning,
      success: BRAND_COLORS.success,
      info: BRAND_COLORS.info,
      // Legacy
      tealHover: 'rgba(27, 129, 135, 0.04)',
    },
    nav: {
      activeIconBg: BRAND_COLORS.teal,
      activeIconColor: '#FFFFFF',
      inactiveIconColor: BRAND_COLORS.darkTeal,
      activeText: BRAND_COLORS.teal,
      inactiveText: BRAND_COLORS.darkTeal,
    },
    surface: {
      sidebar: '#FFFFFF', // same as paper in light mode
      card: '#FFFFFF',
    },
    status: {
      success: BRAND_COLORS.teal,
      warning: BRAND_COLORS.yellow,
    },
    hover: {
      lightTeal: 'rgba(27, 129, 135, 0.04)',
    },
    grey: GREY,
  },
  shape: { borderRadius: 10 }, // matches --r-md; kept identical in light+dark so bare-number sx borderRadius resolves the same in both themes
  typography: {
    fontFamily: 'var(--font-body)',
    // Heading scale — h1-h3 match TYPE_SCALE (brand-tokens.ts) exactly: 26px/18px/16px, weight 700.
    // Font-family kept on body font (Onest), not the documented display font — deliberate choice, not a bug.
    // h4-h6 match MUI defaults so existing pages render correctly.
    // Body and utility variants match MUI defaults for consistency.
    // Source of truth for all font sizes — never hardcode fontSize in sx props.
    h1: { fontWeight: 700, fontSize: '1.625rem', fontFamily: 'var(--font-body)' }, // 26px
    h2: { fontWeight: 700, fontSize: '1.125rem', fontFamily: 'var(--font-body)' }, // 18px
    h3: { fontWeight: 700, fontSize: '1rem',     fontFamily: 'var(--font-body)' }, // 16px
    h4: { fontWeight: 600, fontSize: '1.5rem',   fontFamily: 'var(--font-body)' }, // 24px
    h5: { fontWeight: 600, fontSize: '1.25rem',  fontFamily: 'var(--font-body)' }, // 20px
    h6: { fontWeight: 600, fontSize: '1.125rem', fontFamily: 'var(--font-body)' }, // 18px
    subtitle1: { fontSize: '1rem',      fontWeight: 500 }, // 16px
    subtitle2: { fontSize: '0.875rem',  fontWeight: 600 }, // 14px
    body1:     { fontSize: '1rem' },                       // 16px
    body2:     { fontSize: '0.875rem' },                   // 14px
    caption:   { fontSize: '0.75rem' },                    // 12px
    overline:  { fontSize: '0.6875rem', fontWeight: 700 }, // 11px
    button:    { fontSize: '0.875rem',  fontWeight: 600 }, // 14px
  },
  spacing: 8, // MUI default
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { boxShadow: 'none', backgroundImage: 'none' },
      },
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        // Unified background series styling (areas/lines) inside a bg-series container
        '.bg-series .MuiAreaElement-root': {
          opacity: theme.charts.background.areaOpacity,
        },
        '.bg-series .MuiLineElement-root': {
          opacity: theme.charts.background.lineOpacity,
          strokeWidth: theme.charts.background.lineWidth,
        },
        // Emphasized foreground lines
        '.emphasis-lines .MuiLineElement-root': {
          strokeWidth: 3,
          opacity: 1,
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.18))',
        },
        // Default line contrast for foreground lines
        '.MuiLineElement-root': {
          strokeWidth: 2,
          opacity: 0.9,
        },
        // Softer area by default (foreground areas if any)
        '.MuiAreaElement-root': {
          opacity: 0.22,
        },
        // Remove grey hover band; keep interactions clean
        '.MuiChartsAxisHighlight-root rect': {
          fill: 'transparent',
        },
        // Subtle hover shadow for bars and marks
        '.MuiBarElement-root': {
          transition: 'filter 150ms ease',
        },
        '.MuiBarElement-root:hover': {
          filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.18))',
        },
        '.MuiMarkElement-root': {
          transition: 'filter 150ms ease',
        },
        '.MuiMarkElement-root:hover': {
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.18))',
        },
        // Tooltip look & feel aligned with MUI Paper
        '.MuiChartsTooltip-root': {
          borderRadius: 12,
          boxShadow: theme.shadows[3],
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          padding: '8px 10px',
        },
        // Ensure tooltip series markers are squared and consistent in size
        '.MuiChartsTooltip-mark, .MuiChartsTooltip-series-mark': {
          width: 12,
          height: 12,
          borderRadius: 2,
          boxShadow: 'none',
        },
      }),
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundColor: 'var(--bg-canvas)', borderRadius: 10 }
      }
    },
    MuiButton: {
      defaultProps: { variant: 'contained' },
      styleOverrides: {
        // DESIGN_SYSTEM.md: radius 8px light / 12px dark; hover brightness(0.95) light / brightness(1.05) dark.
        // Only lightTheme's components are ever applied (see theme-cssvars.ts) so the dark variant
        // must be expressed here via theme.applyStyles, not in the separate (dead) darkTheme block.
        root: ({ theme }: { theme: any }) => [
          {
            textTransform: 'none',
            borderRadius: 8,
            transition: BRAND_TRANSITIONS.smooth,
            fontWeight: 600,
            fontSize: '0.8125rem',
            padding: '8px 16px',
            '&:hover': { filter: 'brightness(0.95)' },
          },
          theme.applyStyles('dark', {
            borderRadius: 12,
            '&:hover': { filter: 'brightness(1.05)' },
          }),
        ],
      },
    },
    MuiChip: {
      styleOverrides: {
        // DESIGN_SYSTEM.md: radius 8px light / 14px dark. See MuiButton above re: applyStyles.
        root: ({ theme }: { theme: any }) => [
          {
            fontWeight: 500,
            borderRadius: '8px !important',
            fontSize: '0.75rem',
            '&.MuiChip-colorSuccess':  { backgroundColor: 'var(--success-bg)',  color: 'var(--success-text)',  border: '1px solid var(--success-border)' },
            '&.MuiChip-colorWarning':  { backgroundColor: 'var(--yellow-bg)',   color: 'var(--yellow-text)',   border: '1px solid var(--yellow-border)' },
            '&.MuiChip-colorError':    { backgroundColor: 'var(--danger-bg)',   color: 'var(--danger-text)',   border: '1px solid var(--danger-border)' },
            '&.MuiChip-colorInfo':     { backgroundColor: 'var(--accent-bg)',   color: 'var(--accent-text)',   border: '1px solid var(--accent-border)' },
            '&.MuiChip-colorPrimary':  { backgroundColor: 'var(--accent-bg)',   color: 'var(--accent-text)',   border: '1px solid var(--accent-border)' },
          },
          theme.applyStyles('dark', { borderRadius: '14px !important' }),
        ],
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          ...TABLE_HEAD_TOKENS,
          backgroundColor: 'var(--palette-background-default)',
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'var(--palette-action-hover)',
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: 'var(--accent-text)' },
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          color: 'var(--text-tertiary)',
          '&:hover': { color: 'var(--text-primary)' },
          '&.Mui-selected': { color: 'var(--accent-text)', fontWeight: 600 },
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: any }) => ({
          borderRadius: 10,
          border: '1px solid',
          boxShadow: 'var(--shadow-1)',
          ...(ownerState.severity === 'success' && { backgroundColor: 'var(--success-bg)', color: 'var(--success-text)', borderColor: 'var(--success-border)' }),
          ...(ownerState.severity === 'warning' && { backgroundColor: 'var(--yellow-bg)',  color: 'var(--yellow-text)',  borderColor: 'var(--yellow-border)' }),
          ...(ownerState.severity === 'error'   && { backgroundColor: 'var(--danger-bg)',  color: 'var(--danger-text)',  borderColor: 'var(--danger-border)' }),
          ...(ownerState.severity === 'info'    && { backgroundColor: 'var(--accent-bg)',  color: 'var(--accent-text)',  borderColor: 'var(--accent-border)' }),
        }),
        icon: ({ ownerState }: { ownerState: any }) => ({
          ...(ownerState.severity === 'success' && { color: 'var(--success-text)' }),
          ...(ownerState.severity === 'warning' && { color: 'var(--yellow-text)' }),
          ...(ownerState.severity === 'error'   && { color: 'var(--danger-text)' }),
          ...(ownerState.severity === 'info'    && { color: 'var(--accent-text)' }),
        }),
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          // Row/cell/header border lines follow the theme divider instead of MUI's own default
          '--DataGrid-rowBorderColor': theme.palette.divider,
          ...DATA_GRID_TYPOGRAPHY,
          ...DATA_GRID_ROOT_ALIGNMENT_FIXES,
          // MUI defaults the grid body to its own internal background token
          // rather than theme.palette.background.paper — same class of mismatch
          // already fixed below for the columns/filters/density flyout `paper`.
          backgroundColor: 'var(--bg-canvas)',
          // Globally fix padding for checkboxes to prevent misalignment. Doubled classes
          // out-rank the `cell`/`columnHeader` slot overrides below (justify flex-start),
          // which otherwise left-align the row checkboxes while the header one centers.
          "& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          },
          // The header checkbox sits inside the title/draggable containers, which the
          // columnHeader overrides below left-align — center them so it lines up with the rows.
          "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainerContent": {
            justifyContent: "center",
            padding: 0,
          },
        }),
        columnHeaders: {
          ...TABLE_HEAD_TOKENS,
        },
        columnHeader: {
          ...DATA_GRID_VERTICAL_ALIGN,
          ...DATA_GRID_JUSTIFY_START,
        },
        columnHeaderTitle: {
          ...TABLE_HEAD_TOKENS,
        },
        columnHeaderTitleContainer: {
          ...DATA_GRID_VERTICAL_ALIGN,
          ...DATA_GRID_JUSTIFY_START,
        },
        cell: {
          ...DATA_GRID_TYPOGRAPHY,
          ...DATA_GRID_VERTICAL_ALIGN,
          ...DATA_GRID_JUSTIFY_START,
        },
        footerContainer: {
          ...DATA_GRID_TYPOGRAPHY,
        },
        toolbarContainer: {
          ...DATA_GRID_TYPOGRAPHY,
        },
        // Columns / Filters / Density flyout panel — MUI defaults to
        // theme.palette.background.paper, which is lighter than this app's
        // dark surfaces and reads as a mismatched gray overlay.
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          border: '1px solid var(--line-strong, rgba(255, 255, 255, 0.12))',
          boxShadow: 'var(--shadow-3, 0px 12px 32px rgba(0, 0, 0, 0.36))',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          minWidth: 180, // Ensure adequate space for labels
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            fontWeight: 500,
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        // DESIGN_SYSTEM.md: radius 12px; hover border teal @ 50% alpha; focus border teal, 1px.
        root: {
          borderRadius: 12,
          fontSize: 13,
          backgroundColor: 'var(--bg-canvas)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: 12,
            borderColor: 'var(--line-strong)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(27, 129, 135, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--accent-text)',
            borderWidth: '1px',
          },
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        // Same mismatched-gray issue as the DataGrid panel above — the
        // dropdown Paper defaults to theme.palette.background.paper.
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          border: '1px solid var(--line-strong, rgba(255, 255, 255, 0.12))',
          boxShadow: 'var(--shadow-3, 0px 12px 32px rgba(0, 0, 0, 0.36))',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        color: 'text.primary',
      },
      styleOverrides: {
        caption: {
          fontSize: '0.75rem', // Consistent caption size
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        // DESIGN_SYSTEM.md: radius 12px; hover border teal @ 50% alpha; focus border teal, 1px.
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            fontSize: 13,
            backgroundColor: 'var(--bg-canvas)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--line-strong)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(27, 129, 135, 0.5)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--accent-text)',
              borderWidth: '1px',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            '&.Mui-focused': {
              color: 'var(--accent-text)',
            },
          },
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          transition: `width ${BRAND_TRANSITIONS.duration} ${BRAND_TRANSITIONS.easing}`,
          overflowX: 'hidden',
          borderRadius: 0,
          outline: 'none',
          border: 'none',
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          '&:before': {
            display: 'none', // Remove the default divider
          },
          '&.Mui-expanded': {
            margin: 'auto', // Prevent margin changes when expanded
          }
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          '&.Mui-expanded': {
            minHeight: 'auto', // Prevent height changes when expanded
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        // DESIGN_SYSTEM.md: dialogs use theme.customShadows.dialog, not a generic shadow tier.
        paper: ({ theme }: { theme: any }) => ({
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          boxShadow: theme.customShadows.dialog,
          borderRadius: 14,
          border: '1px solid var(--line)',
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-body)',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          '&.MuiDialogContent-root': {
            paddingTop: '20px !important',
          },
          '.MuiDialogTitle-root + &.MuiDialogContent-root': {
            paddingTop: '20px !important',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          border: '1px solid var(--line-strong)',
          borderRadius: 10,
          boxShadow: 'var(--shadow-2)',
          padding: '4px',
        },
        list: { padding: '4px 0' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.8125rem',
          color: 'var(--text-secondary)',
          margin: '0 4px',
          padding: '8px 12px',
          gap: 8,
          transition: 'background var(--t-instant) var(--ease)',
          '&:hover': {
            backgroundColor: 'var(--bg-hover)',
            color: 'var(--text-primary)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--accent-bg)',
            color: 'var(--accent-text)',
            '&:hover': { backgroundColor: 'var(--accent-bg-strong)' },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          boxShadow: 'var(--shadow-2)',
          border: '1px solid var(--line-strong)',
          borderRadius: 10,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--bg-active)',
          color: 'var(--text-primary)',
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 6,
          border: '1px solid var(--line-strong)',
          boxShadow: 'var(--shadow-1)',
          padding: '5px 10px',
        },
        arrow: { color: 'var(--bg-active)' },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: { bottom: 24, right: 24 },
      },
    },
  },
  charts: {
    legend: {
      markerSize: 12,
      markerRadius: 0,
      gap: 12,
      itemGap: 8,
      fontSize: 14,
      fontWeight: 500,
      marginTop: 8,
    },
    background: {
      areaOpacity: 0.18,
      lineOpacity: 0.45,
      lineWidth: 1,
    },
    bars: {
      categoryGapRatio: 0,
      barGapRatio: 0,
    }
  },
  // Custom sidebar theme
  sidebar: {
    width: BRAND_SPACING.drawerWidth,
    collapsedWidth: BRAND_SPACING.collapsedDrawerWidth,
    iconSize: BRAND_SPACING.iconSize,
    itemHeight: BRAND_SPACING.navigationHeight,
    logoHeight: BRAND_SPACING.logoHeight,
    transition: BRAND_TRANSITIONS.smooth,
    iconBorderRadius: 8,
    activeIconBorderRadius: '50%',
    iconPosition: {
      expanded: 16,
      collapsed: '50%',
    },
    textPadding: '72px',
  },
  // Design interface theme
  design: {
    canvas: {
      backgroundColor: '#f8f9fa',
      borderColor: '#e9ecef',
      gridColor: '#dee2e6',
      height: 700,
      gridSize: 20,
      mailPiece: {
        aspectRatio: '28/19',
        border: '1px solid #ddd', // Thinner border
        borderDark: `1px solid ${BRAND_COLORS.teal}`,
        borderRadius: 1.75, // Consistent with theme (14px / 8)
      }
    },
    toolbar: {
      backgroundColor: '#ffffff',
      iconSize: 24,
      spacing: 8,
      width: 80,
    },
    panel: {
      width: 280,
      backgroundColor: '#ffffff',
      borderColor: '#e9ecef',
    },
  },
  // Journey Builder theme
  journeyBuilder: {
    node: {
      minWidth: JOURNEY_BUILDER.node.minWidth,
      maxWidth: JOURNEY_BUILDER.node.maxWidth,
      padding: JOURNEY_BUILDER.node.padding,
      iconSize: JOURNEY_BUILDER.node.iconSize,
      handleSize: JOURNEY_BUILDER.node.handleSize,
    },
    sidebar: {
      width: JOURNEY_BUILDER.sidebar.width,
      itemPadding: JOURNEY_BUILDER.sidebar.itemPadding,
    },
    detailsPanel: {
      width: JOURNEY_BUILDER.detailsPanel.width,
    },
    edge: {
      strokeWidth: JOURNEY_BUILDER.edge.strokeWidth,
      strokeColor: JOURNEY_BUILDER.edge.strokeColor,
      dashArray: JOURNEY_BUILDER.edge.dashArray,
    },
    toolbox: {
      flatItem: { borderRadius: 8, size: 31 },
      // DESIGN_SYSTEM.md: Audience + Campaign are both Blue (the only blue DATA_VIZ entry is
      // index 4, 'sky'); DirectMail is Amber (already correct). 'action' moved off index 4
      // to index 7 ('sand', the one free/uncollided slot — index 6 is already 'tool').
      colors: {
        audience: { background: DATA_VIZ.light[4].bg, border: DATA_VIZ.light[4].color, text: DATA_VIZ.light[4].color },
        campaign: { background: DATA_VIZ.light[4].bg, border: DATA_VIZ.light[4].color, text: DATA_VIZ.light[4].color },
        directMail: { background: DATA_VIZ.light[1].bg, border: DATA_VIZ.light[1].color, text: DATA_VIZ.light[1].color },
        digitalAd: { background: DATA_VIZ.light[2].bg, border: DATA_VIZ.light[2].color, text: DATA_VIZ.light[2].color },
        condition: { background: DATA_VIZ.light[5].bg, border: DATA_VIZ.light[5].color, text: DATA_VIZ.light[5].color },
        action: { background: DATA_VIZ.light[7].bg, border: DATA_VIZ.light[7].color, text: DATA_VIZ.light[7].color },
        tool: { background: DATA_VIZ.light[6].bg, border: DATA_VIZ.light[6].color, text: DATA_VIZ.light[6].color },
      }
    },
    sections: {
      startIcon: DATA_VIZ.light[0].color,
      channelsIcon: DATA_VIZ.light[3].color,
      conditionsIcon: DATA_VIZ.light[5].color,
      actionsIcon: DATA_VIZ.light[7].color, // kept in sync with 'action' node's new color (moved off index 4)
      toolsIcon: DATA_VIZ.light[6].color,
    },
    quickStart: {
      background: '#EFF6FF',
      border: '#BFDBFE',
      icon: '#60a5fa',
      title: '#1E3A8A',
      text: '#1D4ED8',
    }
  }
})

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: BRAND_COLORS.teal,       // #1B8187 — brand teal across all modes
      lighter: BRAND_COLORS.mediumTeal,
      darker: BRAND_COLORS.deepTeal,
      mainChannel: '27 129 135',
      lighterChannel: '58 175 169',
      darkerChannel: '13 92 97'
    },
    secondary: {
      main: BRAND_COLORS.lightYellow,
      lighter: BRAND_COLORS.lightYellow,
      darker: BRAND_COLORS.yellow,
      mainChannel: '252 242 141',
      lighterChannel: '252 242 141',
      darkerChannel: '236 191 45'
    },
    info: {
      lighter: '#004D40',
      light: '#00796B',
      main: '#009688',
      dark: '#4DB6AC',
      darker: '#B2DFDB',
      contrastText: '#FFFFFF',
      mainChannel: '0 150 136',
      lightChannel: '77 182 172',
      darkChannel: '0 121 107',
    },
    background: { default: '#0A1418', paper: '#0F1B22' },
    divider: 'rgba(232, 238, 240, 0.08)',
    text: {
      primary: '#E8EEF0',
      secondary: '#CFD8DC', // Lighter for better visibility
      disabled: '#90A4AE', // Lighter for better visibility
    },
    // Custom brand colors accessible via theme.palette
    brand: {
      // Teal family
      darkTeal: BRAND_COLORS.darkTeal,
      deepTeal: BRAND_COLORS.deepTeal,
      teal: BRAND_COLORS.teal,
      mediumTeal: BRAND_COLORS.mediumTeal,
      lightTeal: BRAND_COLORS.lightTeal,
      deepRed: BRAND_COLORS.deepRed,
      // Yellow family
      yellow: BRAND_COLORS.yellow,
      softYellow: BRAND_COLORS.softYellow,
      lightYellow: BRAND_COLORS.lightYellow,
      // Neutrals
      white: BRAND_COLORS.white,
      lightGray: BRAND_COLORS.lightGray,
      darkGray: BRAND_COLORS.darkGray,
      // Alert colors (exceptions)
      error: BRAND_COLORS.error,
      warning: BRAND_COLORS.warning,
      success: BRAND_COLORS.success,
      info: BRAND_COLORS.info,
      // Legacy
      tealHover: 'rgba(27, 129, 135, 0.08)',
    },
    nav: {
      activeIconBg: BRAND_COLORS.teal,
      activeIconColor: '#ffffff',
      inactiveIconColor: BRAND_COLORS.darkModeSecondary,
      activeText: '#ffffff',
      inactiveText: BRAND_COLORS.darkModeSecondary,
    },
    surface: {
      sidebar: '#0F1B22', // --bg-canvas dark
      card: '#0F1B22',    // --bg-canvas dark
    },
    status: {
      success: BRAND_COLORS.teal,
      warning: BRAND_COLORS.lightYellow,
    },
    hover: {
      lightTeal: 'rgba(27, 129, 135, 0.08)',
    },
    grey: DARK_GREY,
  },
  shape: { borderRadius: 10 }, // matches --r-md; kept identical in light+dark so bare-number sx borderRadius resolves the same in both themes
  typography: {
    fontFamily: 'var(--font-body)',
    // Same scale as light theme — only heading colors differ.
    h1: { fontWeight: 700, fontSize: '1.625rem', fontFamily: 'var(--font-body)' },
    h2: { fontWeight: 700, fontSize: '1.125rem', fontFamily: 'var(--font-body)' },
    h3: { fontWeight: 700, fontSize: '1rem',     fontFamily: 'var(--font-body)' },
    h4: { fontWeight: 600, fontSize: '1.5rem',   fontFamily: 'var(--font-body)', color: '#ffffff' },
    h5: { fontWeight: 600, fontSize: '1.25rem',  fontFamily: 'var(--font-body)', color: '#ffffff' },
    h6: { fontWeight: 600, fontSize: '1.125rem', fontFamily: 'var(--font-body)', color: '#ffffff' },
    subtitle1: { fontSize: '1rem',      fontWeight: 500 },
    subtitle2: { fontSize: '0.875rem',  fontWeight: 600 },
    body1:     { fontSize: '1rem' },
    body2:     { fontSize: '0.875rem' },
    caption:   { fontSize: '0.75rem' },
    overline:  { fontSize: '0.6875rem', fontWeight: 700 },
    button:    { fontSize: '0.875rem',  fontWeight: 600 },
  },
  spacing: 8, // MUI default
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { boxShadow: 'none', backgroundImage: 'none' },
      },
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        // Unified background series styling (areas/lines) inside a bg-series container
        '.bg-series .MuiAreaElement-root': {
          opacity: theme.charts.background.areaOpacity,
        },
        '.bg-series .MuiLineElement-root': {
          opacity: theme.charts.background.lineOpacity,
          strokeWidth: theme.charts.background.lineWidth,
        },
        // Emphasized foreground lines (slightly stronger shadow on dark)
        '.emphasis-lines .MuiLineElement-root': {
          strokeWidth: 3,
          opacity: 1,
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
        },
        // Default line contrast for foreground lines
        '.MuiLineElement-root': {
          strokeWidth: 2,
          opacity: 0.9,
        },
        // Softer area by default (foreground areas if any)
        '.MuiAreaElement-root': {
          opacity: 0.22,
        },
        // Remove grey hover band; keep interactions clean
        '.MuiChartsAxisHighlight-root rect': {
          fill: 'transparent',
        },
        // Subtle hover shadow for bars and marks
        '.MuiBarElement-root': {
          transition: 'filter 150ms ease',
        },
        '.MuiBarElement-root:hover': {
          filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.18))',
        },
        '.MuiMarkElement-root': {
          transition: 'filter 150ms ease',
        },
        '.MuiMarkElement-root:hover': {
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.18))',
        },
        // Tooltip look & feel aligned with MUI Paper
        '.MuiChartsTooltip-root': {
          borderRadius: 12,
          boxShadow: theme.shadows[3],
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          padding: '8px 10px',
        },
        '.MuiChartsTooltip-mark, .MuiChartsTooltip-series-mark': {
          width: 12,
          height: 12,
          borderRadius: 2,
          boxShadow: 'none',
        },
      }),
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundColor: 'var(--bg-canvas)', borderRadius: 10 }
      }
    },
    MuiButton: {
      defaultProps: { variant: 'contained' },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10, // matches --r-md; see docs/DESIGN.md's documented button anatomy
          transition: BRAND_TRANSITIONS.smooth,
          fontWeight: 600,
          fontSize: '0.8125rem',
          padding: '8px 16px',
          '&:hover': {
            filter: 'brightness(1.05)'
          }
        },
        containedPrimary: {
          backgroundColor: BRAND_COLORS.teal, // #1B8187 — match brand teal used in light mode
          '&:hover': {
            backgroundColor: BRAND_COLORS.teal,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: '14px !important', // Force consistent border radius over external CSS
          fontSize: '0.75rem'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          ...TABLE_HEAD_TOKENS,
          backgroundColor: '#0A1418',
          borderBottom: `1px solid rgba(232, 238, 240, 0.12)`,
        },
        root: {
          borderBottom: `1px solid rgba(232, 238, 240, 0.06)`,
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(27,129,135,0.08)'
          }
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          // Row/cell/header border lines follow the theme divider instead of MUI's own default
          '--DataGrid-rowBorderColor': theme.palette.divider,
          ...DATA_GRID_TYPOGRAPHY,
          ...DATA_GRID_ROOT_ALIGNMENT_FIXES,
          // MUI defaults the grid body to its own internal background token
          // rather than theme.palette.background.paper — same class of mismatch
          // already fixed below for the columns/filters/density flyout `paper`.
          backgroundColor: 'var(--bg-canvas)',
        }),
        columnHeaders: {
          ...TABLE_HEAD_TOKENS,
        },
        columnHeader: {
          ...DATA_GRID_VERTICAL_ALIGN,
          ...DATA_GRID_JUSTIFY_START,
        },
        columnHeaderTitle: {
          ...TABLE_HEAD_TOKENS,
        },
        columnHeaderTitleContainer: {
          ...DATA_GRID_VERTICAL_ALIGN,
          ...DATA_GRID_JUSTIFY_START,
        },
        cell: {
          ...DATA_GRID_TYPOGRAPHY,
          ...DATA_GRID_VERTICAL_ALIGN,
          ...DATA_GRID_JUSTIFY_START,
        },
        footerContainer: {
          ...DATA_GRID_TYPOGRAPHY,
        },
        toolbarContainer: {
          ...DATA_GRID_TYPOGRAPHY,
        },
        // Columns / Filters / Density flyout panel — MUI defaults to
        // theme.palette.background.paper, which is lighter than this app's
        // dark surfaces and reads as a mismatched gray overlay.
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          border: '1px solid var(--line-strong, rgba(255, 255, 255, 0.12))',
          boxShadow: 'var(--shadow-3, 0px 12px 32px rgba(0, 0, 0, 0.36))',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          minWidth: 180, // Ensure adequate space for labels
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            fontWeight: 500,
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: 13,
          backgroundColor: 'var(--bg-canvas)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: 6,
            borderColor: 'var(--line-strong)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--accent-border)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--accent-text)',
            borderWidth: '2px',
          },
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        // Same mismatched-gray issue as the DataGrid panel above — the
        // dropdown Paper defaults to theme.palette.background.paper.
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          border: '1px solid var(--line-strong, rgba(255, 255, 255, 0.12))',
          boxShadow: 'var(--shadow-3, 0px 12px 32px rgba(0, 0, 0, 0.36))',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        color: 'text.primary'
      },
      styleOverrides: {
        caption: {
          fontSize: '0.75rem'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            fontSize: 13,
            backgroundColor: 'var(--bg-canvas)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--line-strong)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--accent-border)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--accent-text)',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            '&.Mui-focused': {
              color: 'var(--accent-text)',
            },
          },
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          transition: `width ${BRAND_TRANSITIONS.duration} ${BRAND_TRANSITIONS.easing}`,
          overflowX: 'hidden',
          borderRadius: 0,
          outline: 'none',
          border: 'none',
          backgroundColor: '#09222d', // use subtle darkening instead of much darker tone
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          '&:before': {
            display: 'none', // Remove the default divider
          },
          '&.Mui-expanded': {
            margin: 'auto', // Prevent margin changes when expanded
          }
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          '&.Mui-expanded': {
            minHeight: 'auto', // Prevent height changes when expanded
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          boxShadow: 'var(--shadow-3)',
          borderRadius: 14,
          border: '1px solid var(--line)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-body)',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          '&.MuiDialogContent-root': {
            paddingTop: '20px !important',
          },
          '.MuiDialogTitle-root + &.MuiDialogContent-root': {
            paddingTop: '20px !important',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          border: '1px solid var(--line-strong)',
          borderRadius: 10,
          boxShadow: 'var(--shadow-2)',
          padding: '4px',
        },
        list: { padding: '4px 0' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.8125rem',
          color: 'var(--text-secondary)',
          margin: '0 4px',
          padding: '8px 12px',
          gap: 8,
          transition: 'background var(--t-instant) var(--ease)',
          '&:hover': {
            backgroundColor: 'var(--bg-hover)',
            color: 'var(--text-primary)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--accent-bg)',
            color: 'var(--accent-text)',
            '&:hover': { backgroundColor: 'var(--accent-bg-strong)' },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          boxShadow: 'var(--shadow-2)',
          border: '1px solid var(--line-strong)',
          borderRadius: 10,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--bg-active)',
          color: 'var(--text-primary)',
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 6,
          border: '1px solid var(--line-strong)',
          boxShadow: 'var(--shadow-1)',
          padding: '5px 10px',
        },
        arrow: { color: 'var(--bg-active)' },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: { bottom: 24, right: 24 },
      },
    },
  },
  charts: {
    legend: {
      markerSize: 12,
      markerRadius: 0,
      gap: 12,
      itemGap: 8,
      fontSize: 14,
      fontWeight: 500,
      marginTop: 8,
    },
    background: {
      areaOpacity: 0.18,
      lineOpacity: 0.45,
      lineWidth: 1,
    },
    bars: {
      categoryGapRatio: 0,
      barGapRatio: 0,
    }
  },
  // Custom sidebar theme
  sidebar: {
    width: BRAND_SPACING.drawerWidth,
    collapsedWidth: BRAND_SPACING.collapsedDrawerWidth,
    iconSize: BRAND_SPACING.iconSize,
    itemHeight: BRAND_SPACING.navigationHeight,
    logoHeight: BRAND_SPACING.logoHeight,
    transition: BRAND_TRANSITIONS.smooth,
    iconBorderRadius: 8,
    activeIconBorderRadius: '50%',
    iconPosition: {
      expanded: 16,
      collapsed: '50%',
    },
    textPadding: '72px',
  },
  // Design interface theme
  design: {
    canvas: {
      backgroundColor: BRAND_COLORS.darkTeal,
      borderColor: BRAND_COLORS.teal,
      // Softer grid dots for a more muted look
      gridColor: 'rgba(87, 194, 190, 0.22)',
      height: 700,
      gridSize: 20,
      mailPiece: {
        aspectRatio: '28/19',
        border: '1px solid #ddd', // Thinner border
        borderDark: `1px solid ${BRAND_COLORS.teal}`,
        borderRadius: 1.75, // Consistent with theme (14px / 8)
      }
    },
    toolbar: {
      backgroundColor: '#0f2c3a', // Slightly lighter brand color
      iconSize: 24,
      spacing: 8,
      width: 80,
    },
    panel: {
      width: 280,
      backgroundColor: '#0f2c3a', // Use brand color instead of grey
      borderColor: BRAND_COLORS.teal,
    },
  },
  // Journey Builder theme
  journeyBuilder: {
    node: {
      minWidth: JOURNEY_BUILDER.node.minWidth,
      maxWidth: JOURNEY_BUILDER.node.maxWidth,
      padding: JOURNEY_BUILDER.node.padding,
      iconSize: JOURNEY_BUILDER.node.iconSize,
      handleSize: JOURNEY_BUILDER.node.handleSize,
    },
    sidebar: {
      width: JOURNEY_BUILDER.sidebar.width,
      itemPadding: JOURNEY_BUILDER.sidebar.itemPadding,
    },
    detailsPanel: {
      width: JOURNEY_BUILDER.detailsPanel.width,
    },
    edge: {
      strokeWidth: JOURNEY_BUILDER.edge.strokeWidth,
      strokeColor: BRAND_COLORS.darkModeSecondary,
      dashArray: JOURNEY_BUILDER.edge.dashArray,
    },
    toolbox: {
      flatItem: { borderRadius: 8, size: 31 },
      // DESIGN_SYSTEM.md: Audience + Campaign are both Blue (the only blue DATA_VIZ entry is
      // index 4, 'sky'); DirectMail is Amber (already correct). 'action' moved off index 4
      // to index 7 ('sand', the one free/uncollided slot — index 6 is already 'tool').
      colors: {
        audience: { background: DATA_VIZ.dark[4].bg, border: DATA_VIZ.dark[4].color, text: DATA_VIZ.dark[4].color },
        campaign: { background: DATA_VIZ.dark[4].bg, border: DATA_VIZ.dark[4].color, text: DATA_VIZ.dark[4].color },
        directMail: { background: DATA_VIZ.dark[1].bg, border: DATA_VIZ.dark[1].color, text: DATA_VIZ.dark[1].color },
        digitalAd: { background: DATA_VIZ.dark[2].bg, border: DATA_VIZ.dark[2].color, text: DATA_VIZ.dark[2].color },
        condition: { background: DATA_VIZ.dark[5].bg, border: DATA_VIZ.dark[5].color, text: DATA_VIZ.dark[5].color },
        action: { background: DATA_VIZ.dark[7].bg, border: DATA_VIZ.dark[7].color, text: DATA_VIZ.dark[7].color },
        tool: { background: DATA_VIZ.dark[6].bg, border: DATA_VIZ.dark[6].color, text: DATA_VIZ.dark[6].color },
      }
    },
    sections: {
      startIcon: DATA_VIZ.dark[0].color,
      channelsIcon: DATA_VIZ.dark[3].color,
      conditionsIcon: DATA_VIZ.dark[5].color,
      actionsIcon: DATA_VIZ.dark[7].color, // kept in sync with 'action' node's new color (moved off index 4)
      toolsIcon: DATA_VIZ.dark[6].color,
    },
    quickStart: {
      background: '#0e2330',
      border: '#164b5d',
      icon: '#60a5fa',
      title: '#dbeafe',
      text: '#93c5fd',
    }
  }
})

/**
 * Brand Constants for External Use
 * 
 * These constants can be imported separately if you need to access
 * brand colors or spacing values outside of the theme system.
 */
export { BRAND_COLORS, BRAND_SPACING, BRAND_TRANSITIONS, BRAND_BREAKPOINTS, JOURNEY_BUILDER }
